import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomDropdown from "../../components/shared/dropdown/CustomDropdown";
import DropdownCheckbox from "../../components/shared/dropdown/DropdownCheckbox";
import Button from "../../components/shared/buttons/button";
import { CloseSvg } from "../../svgFiles/CloseSvg";
import {
  setSearch,
  updateOrdersFilterSearch,
  setFilterPanelOpen,
} from "../../app/orderApi/filterSlice";
import UploadOrderSheetModal from "../../components/shared/modalContent/UploadOrderSheetModal";
import { useAuth } from "../auth/AuthContext";
import { P } from "../../utils/permissions";

export const FILTER_KEY_MAP = {
  Status: "Status",
  "Lens type": "lensGroup",
  "Prescription type": "prescriptionType",
  "Mounting type": "LensMountType",
  "Rx submission type": "prescriptionMethod",
  "Glasses version": "CaptifyVersion",
};

const restoreFiltersFromSearch = (search) => {
  if (!search || typeof search !== "object") {
    return { filterValues: {}, selectedFilters: [] };
  }

  const restoredValues = {};
  const restoredFilters = [];

  Object.entries(FILTER_KEY_MAP).forEach(([label, key]) => {
    const value = search[key];
    if (
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      restoredValues[key] = value;
      restoredFilters.push(label);
    }
  });

  if (search.orderSheet) {
    restoredFilters.push("Upload order number sheet");
  }

  return { filterValues: restoredValues, selectedFilters: restoredFilters };
};

const FilterManager = ({ selectedIds, onClearAllFilters, setRemoveSelection, ordersLoading = false }) => {
  const [showUploadSheetModal, setShowUploadSheetModal] = useState(false);
  const { hasPermission } = useAuth();
  const canUploadSheet = hasPermission(P.ORDERS_UPLOAD_SHEET);
  const dispatch = useDispatch();
  const search = useSelector((state) => state.filters.search);
  const restored = restoreFiltersFromSearch(search);
  const [selectedFilters, setSelectedFilters] = useState(
    restored.selectedFilters
  );
  const [filterValues, setFilterValues] = useState(restored.filterValues);
  const [uploadedFile, setUploadedFile] = useState(search?.orderSheet ?? null);

  const filterOptions = [
    { value: "Status", label: "Status" },
    { value: "Lens type", label: "Lens type" },
    { value: "Prescription type", label: "Prescription type" },
    { value: "Mounting type", label: "Mounting type" },
    { value: "Rx submission type", label: "Rx submission type" },
    { value: "Glasses version", label: "Glasses version" },
    ...(canUploadSheet
      ? [{ value: "Upload order number sheet", label: "Upload order number sheet" }]
      : []),
  ];

  const optionsMap = {
    Status: [
      { value: "0", label: "00 | Missing Rx" },
      { value: "1", label: "01 | Unfilled Rx" },
      { value: "2", label: "02 | Ready to make" },
      { value: "3", label: "03 | Lenses sent" },
      { value: "4", label: "04 | Ready to assemble" },
      { value: "5", label: "05 | Ready to ship" },
      { value: "6", label: "06 | Shipped" },
      { value: "7", label: "07 | Delivered" },
      { value: "999", label: "999 | Cancelled" },
    ],
    "Rx submission type": [
      { value: "Uploaded", label: "Uploaded" },
      { value: "Entered Manually", label: "Enter Manually" },
      { value: "Email Later", label: "Unsubmitted" },
    ],
    "Glasses version": [
      { value: "Captify Pro", label: "Captify Pro" },
      { value: "Captify Myvu", label: "Captify Myvu" },
    ],
    "Mounting type": [
      { value: "bonded", label: "Bonded" },
      { value: "inserts", label: "Inserts" },
    ],
    "Prescription type": [
      { value: "Single vision", label: "Single vision" },
      { value: "Reading Lenses", label: "Reading" },
      { value: "Progressive Lenses", label: "Progressive" },
    ],
    "Lens type": [
      { value: "Clear Lenses", label: "Clear" },
      { value: "Blue Light Filtering Lenses", label: "Blue light filter" },
    ],
    "Upload order number sheet": [],
  };

  // Keep UI in sync with Redux search, but preserve filters the user added
  // that don't have a value yet (so selecting one dropdown doesn't remove the others).
  useEffect(() => {
    const next = restoreFiltersFromSearch(search);
    setFilterValues(next.filterValues);
    setSelectedFilters((prev) => {
      const merged = [];
      const seen = new Set();
      [...prev, ...next.selectedFilters].forEach((filter) => {
        if (seen.has(filter)) return;
        seen.add(filter);
        merged.push(filter);
      });
      return merged;
    });
    if (search?.orderSheet) {
      setUploadedFile(search.orderSheet);
    }
    if (next.selectedFilters.length > 0) {
      dispatch(setFilterPanelOpen(true));
    }
  }, [search, dispatch]);

  const handleDropdownSelect = (selectedValues, label) => {
    const filterKey = FILTER_KEY_MAP[label];
    if (!filterKey) return;

    const updatedFilters = { ...filterValues, [filterKey]: selectedValues };
    setFilterValues(updatedFilters);

    dispatch(
      updateOrdersFilterSearch({
        [filterKey]: selectedValues,
      })
    );
    setRemoveSelection(true);
  };

  const handleAddFilter = (option) => {
    if (selectedFilters.includes(option.label)) return;

    setSelectedFilters((prev) => [...prev, option.label]);

    if (option.label === "Upload order number sheet") {
      setShowUploadSheetModal(true);
    }
  };

  const handleClearAll = () => {
    setSelectedFilters([]);
    setFilterValues({});
    setUploadedFile(null);
    dispatch(
      updateOrdersFilterSearch({
        Status: undefined,
        lensGroup: undefined,
        prescriptionType: undefined,
        LensMountType: undefined,
        prescriptionMethod: undefined,
        CaptifyVersion: undefined,
        orderSheet: undefined,
        ShopifyItemID: selectedIds,
      })
    );
    onClearAllFilters();
  };

  const handleClearFilter = (filter) => {
    const filterKey = FILTER_KEY_MAP[filter];
    const updatedFilters = { ...filterValues };

    if (filterKey) {
      delete updatedFilters[filterKey];
    }
    if (filter === "Upload order number sheet") {
      setUploadedFile(null);
      dispatch(updateOrdersFilterSearch({ orderSheet: undefined }));
    } else if (filterKey) {
      dispatch(updateOrdersFilterSearch({ [filterKey]: undefined }));
    }

    setFilterValues(updatedFilters);
    setRemoveSelection(false);
    setSelectedFilters((prev) => prev.filter((f) => f !== filter));
  };

  const availableFilterOptions = filterOptions.filter(
    (opt) => !selectedFilters.includes(opt.label)
  );

  return (
    <>
      {showUploadSheetModal && (
        <UploadOrderSheetModal
          show={showUploadSheetModal}
          setShow={setShowUploadSheetModal}
          setfile={setUploadedFile}
          uploadedFile={uploadedFile}
        />
      )}

      {selectedFilters.map((filter) => (
        <div key={filter} className="d-flex align-items-center gap-2">
          {filter === "Upload order number sheet" ? (
            <div className="d-flex align-items-center gap-2 orderFile">
              <span>
                {uploadedFile
                  ? uploadedFile.name || uploadedFile
                  : "No file uploaded"}
              </span>
              <CloseSvg onClick={() => handleClearFilter(filter)} />
            </div>
          ) : (
            <DropdownCheckbox
              options={optionsMap[filter]}
              selectedValues={filterValues[FILTER_KEY_MAP[filter]] || []}
              onSelect={(vals) => handleDropdownSelect(vals, filter)}
              onClear={() => handleClearFilter(filter)}
              label={filter}
              loading={ordersLoading}
              autoCloseAfterLoad
            />
          )}
        </div>
      ))}

      {availableFilterOptions.length > 0 && (
        <div>
          <CustomDropdown
            label="Add Filter"
            options={availableFilterOptions}
            onSelect={handleAddFilter}
            className="add-filter"
            lockLabel
          />
        </div>
      )}

      {selectedFilters.length > 0 && (
        <div>
          <Button
            icon={<CloseSvg />}
            label="Clear All"
            type="button"
            className="cancel bg-white text-black border-0 flex-row-reverse px-2"
            onClick={handleClearAll}
          />
        </div>
      )}
    </>
  );
};

export default FilterManager;
